<template>
  <div class="zero-section-geocoder-map">
    <div class="w-lg-60 mx-3 mx-sm-6 mx-lg-auto pt-0 pt-lg-3">
      <form class="position-relative z-index-2"
            :action="actionUrl"
            autocomplete="off"
            @submit.prevent="submitForm"
            method="get"
      >
        <div class="input-group">
          <div ref="geocoder" id="geocoder" class="geocoder"></div>
          <div class="input-group-append">
            <button class="btn btn-lg btn-primary rounded-right-pill" type="submit" aria-label="Szukaj...">
              <i class="fas fa-search mr-lg-2"></i> <span class="d-none d-lg-inline-block">Szukaj</span>
            </button>
          </div>
        </div>
        <input type="hidden" name="_token" :value="token">
        <input v-if="normalizedPresetProductType !== ''" type="hidden" name="q[product_type][]" :value="normalizedPresetProductType">
      </form>
    </div>
  </div>
</template>

<script>
import xsrfUtils from '@/core/utils/xsrf-utils';

import Translator from 'bazinga-translator';
import store from '@/core/services/store';
import MapboxService from '@/core/services/mapbox.service';
import GeocoderSuggestionsMixin from '@/core/mixins/geocoder-suggestions.mixin';
import PersistentStoreProxyService, {
  SET_SEARCH_PARAMS,
  GET_SEARCH_PARAMS
} from '@/core/services/persistent-store-proxy.service';

export default {
  name: 'ZeroSectionGeocoderStandalone',
  mixins: [GeocoderSuggestionsMixin],
  props: {
    longitude: {
      type: String,
      default: '17.0297824',
    },
    latitude: {
      type: String,
      default: '51.1096513',
    },
    mapboxApiKey: {
      type: String,
      default: '',
    },
    actionRoute: {
      type: String,
      default: '',
    },
    allowedMapboxVoivodships: {
      type: Array,
      default() {
        return [];
      },
    },
    presetProductType: {
      type: String,
      default: '',
    },
    defaultSearchRadius: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      default: '',
    },
    submitPlace: {
      type: String,
      default: '',
    },
    preferDefaultLocationOnSubmit: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      spatialPt: '',
      token: '',
      place: '',
      selectedSearchRadius: null,
      manualSearchPhrase: '',
      lastInteractionWasGeocoderSelection: false,
      geocoderInitialized: false,
      initialPosition: MapboxService.LngLat(
        this.longitude,
        this.latitude,
      ),
      position: MapboxService.LngLat(
        this.longitude,
        this.latitude,
      ),
    };
  },
  computed: {
    mapboxAvailable() {
      return MapboxService.apiAvailable();
    },
    actionUrl() {
      return JsRouting.generate(this.actionRoute, {}, true);
    },
    normalizedPresetProductType() {
      const value = `${this.presetProductType || ''}`.trim().toLowerCase();
      const allowedValues = ['accommodation', 'food', 'handicraftandservices'];

      return allowedValues.includes(value) ? value : '';
    },
    normalizedDefaultSearchRadius() {
      const radius = Number.parseFloat(this.defaultSearchRadius);

      return Number.isFinite(radius) && radius > 0 ? radius : null;
    },
    normalizedSubmitPlace() {
      return `${this.submitPlace || ''}`.trim();
    },
  },
  watch: {
    position(lngLat) {
      if (typeof lngLat !== 'undefined') {
        this.spatialPt = `${lngLat.lat},${lngLat.lng}`;
      }
    },
  },
  created() {
    this.geocoder = null;
  },
  mounted() {
    const self = this;
    if ($(this.$el)[0].nodeName === 'FORM') {
      this.$form = $(this.$el);
    } else {
      this.$form = $(this.$el).find('form');
    }
    self.init();
  },
  methods: {
    resolveAllowedMapboxVoivodships() {
      const globalSource = (typeof GLOBAL !== 'undefined' && GLOBAL && GLOBAL.mapboxGeocoderAllowedVoivodships)
        ? GLOBAL.mapboxGeocoderAllowedVoivodships
        : [];
      let list = this.normalizeVoivodshipSourceToList(this.allowedMapboxVoivodships);
      if (list.length === 0) {
        list = this.normalizeVoivodshipSourceToList(globalSource);
      }

      console.log('[GeocoderVoivodshipDebug] zero-section allowed-mapbox-voivodships input', {
        propValue: this.allowedMapboxVoivodships,
        resolvedListBeforeNormalize: list,
      });

      return Array.from(new Set(
        list
          .map((item) => this.normalizeRegionLabel(item))
          .filter((item) => item !== '')
      ));
    },

    resolveAutosuggestLocationPoint() {
      const lat = Number.parseFloat(this.position && this.position.lat);
      const lng = Number.parseFloat(this.position && this.position.lng);
      if (Number.isFinite(lat) && Number.isFinite(lng)) {
        return {
          lat,
          lng,
        };
      }

      return {
        lat: Number.parseFloat(this.latitude),
        lng: Number.parseFloat(this.longitude),
      };
    },

    resolveExternalSuggestionExtraParams(params) {
      if (this.normalizedPresetProductType === '') {
        return;
      }

      params.append('product_type', this.normalizedPresetProductType);
    },

    async getGeolocationPermissionState() {
      if (navigator.permissions && navigator.permissions.query) {
        try {
          const result = await navigator.permissions.query({name: 'geolocation'});
          if (result && typeof result.state === 'string') {
            return result.state;
          }
        } catch (e) {
          // ignore and fallback below
        }
      }
      return 'unknown';
    },

    resolvePlaceForStorage(rawPlace) {
      const normalized = `${rawPlace || ''}`.trim();
      if (normalized === '' || normalized === '-' || normalized === '_') {
        return 'aktualna lokalizacja';
      }

      return normalized;
    },

    normalizeStoredSearchParams(rawSearchParams) {
      const searchParams = rawSearchParams || {};
      const lat = parseFloat(searchParams.lat);
      const lng = parseFloat(searchParams.lng);
      if (Number.isFinite(lat) && Number.isFinite(lng)) {
        return {
          place: searchParams.place || '',
          lat: lat,
          lng: lng,
          spatial_pt: `${lat},${lng}`,
          phrase: this.normalizeSearchPhrase(searchParams.phrase || ''),
        };
      }

      return null;
    },

    async init() {
      const self = this;

      MapboxService.init();

      try {
        await this.detectInitialSearchLocation();
        this.initGeocoder();
      } catch (err) {
        const message = err?.responseJSON?.message
          || err?.message
          || err?.responseText
          || String(err);
        console.error(message); // TypeError: failed to fetch
      }
    },

    trans(key, domain, params, multipleCount) {
        if (multipleCount) {
            return Translator.transChoice(key, multipleCount, params || {}, domain || 'AppProduct');
        } else {
            return Translator.trans(key, params || {}, domain || 'AppProduct');
        }
    },

    async detectInitialSearchLocation() {
      const self = this;

      // check mapbox api
      if (!MapboxService.apiAvailable()) {
        return;
      }

      if (this.preferDefaultLocationOnSubmit) {
        self.initialPosition = MapboxService.LngLat(
          this.longitude,
          this.latitude,
        );
        self.position = MapboxService.LngLat(
          self.initialPosition.lng,
          self.initialPosition.lat,
        );
        return;
      }

      // Keep homepage autosuggest aligned with /search by reusing persisted search coordinates.
      const storedSearchParams = this.normalizeStoredSearchParams(PersistentStoreProxyService[GET_SEARCH_PARAMS]());
      if (storedSearchParams) {
        self.initialPosition = MapboxService.LngLat(
          storedSearchParams.lng,
          storedSearchParams.lat,
        );
        self.position = MapboxService.LngLat(
          self.initialPosition.lng,
          self.initialPosition.lat,
        );

        return;
      }

      const geolocationPermissionState = await this.getGeolocationPermissionState();
      // determine the location for the search
      if (geolocationPermissionState === 'granted' && navigator.geolocation) {
        // fetch user position from the browser
        await navigator.geolocation.getCurrentPosition((position) => {
          self.initialPosition = MapboxService.LngLat(
            position.coords.longitude,
            position.coords.latitude,
          );
        });
      } else {
        // static fallback
        console.info('No geolocation data available');
        self.initialPosition = MapboxService.LngLat(
          this.longitude,
          this.latitude,
        );
      }
      self.position = MapboxService.LngLat(
        self.initialPosition.lng,
        self.initialPosition.lat,
      );
    },

    /**
     * @see https://github.com/mapbox/mapbox-gl-geocoder/blob/master/API.md
     */
    initGeocoder() {
      const self = this;

      // check mapbox api
      if (!MapboxService.apiAvailable()) {
        return false;
      }

      if (!this.$refs['geocoder']) {
        return false;
      }

      this.geocoder = MapboxService.createGeocoder({
        language: 'pl',
        types: 'place',
        countries: 'pl',
        limit: this.resolveGeocoderSuggestionRenderLimit(),
        placeholder: this.placeholder || this.trans('form.search.where-do-you-want-to-search'),
        render: (feature) => this.renderGeocoderSuggestion(feature),
        filter: (feature) => this.isMapboxSuggestionAllowed(feature),
      });
      if (this.geocoder && typeof this.geocoder._renderNoResults === 'function' && typeof this.geocoder._renderMessage === 'function') {
        this.geocoder._renderNoResults = () => {
          const messageText = this.trans('product.search.geocoder.no_results_fulltext_hint');
          const messageHtml = `<div class="mapbox-gl-geocoder--error mapbox-gl-geocoder--no-results">${messageText}</div>`;
          this.geocoder._renderMessage(messageHtml);
        };
      }

      this.geocoder.addTo('#geocoder');
      this.bindGeocoderInputTracking();
      this.geocoder.on('results', (e) => {
        this.mergeExternalSuggestionsIntoResultList(e);
      });

      // autocomplete listener
      this.geocoder.on('result', (e) => {
        const {result} = e;
        const resultProperties = result && result.properties ? result.properties : {};
        if (this.isRefineSuggestion(result)) {
          this.preserveTypedPhraseAfterFulltextSelection(result);
          return;
        }
        const isFulltextSuggestion = resultProperties.isFulltextSuggestion === true;
        if (isFulltextSuggestion) {
          this.preserveTypedPhraseAfterFulltextSelection(result);
        }
        if (isFulltextSuggestion && this.submitAggregateSuggestionAsSearch(result)) {
          return;
        }
        if (isFulltextSuggestion && this.openFulltextSuggestionInNewTab(result)) {
          return;
        }
        const isTourismRegionSuggestion = resultProperties.isTourismRegionSuggestion === true;
        if (isTourismRegionSuggestion) {
          const coordinates = result && result.geometry && Array.isArray(result.geometry.coordinates)
            ? result.geometry.coordinates
            : [];
          const lng = Number.parseFloat(coordinates[0]);
          const lat = Number.parseFloat(coordinates[1]);
          if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
            return;
          }
          const radiusKm = Number.parseInt(resultProperties.tourismRegionRadiusKm, 10);
          self.place = result.text || result.place_name || '';
          self.selectedSearchRadius = Number.isFinite(radiusKm) && radiusKm > 0 ? radiusKm : null;
          this.lastInteractionWasGeocoderSelection = true;
          self.position = MapboxService.LngLat(lng, lat);
          this.$emit('geocode', self.position.lat, self.position.lng, self.place, self.selectedSearchRadius);
          this.submitForm();
          return;
        }
        const resultId = `${result && result.id ? result.id : ''}`;
        const resultType = resultId.indexOf('.') > -1 ? resultId.split('.')[0] : resultId;

        this.lastInteractionWasGeocoderSelection = !isFulltextSuggestion;
        if (!isFulltextSuggestion) {
          self.selectedSearchRadius = null;
        }
        if (isFulltextSuggestion) {
          const fulltextPhrase = this.normalizeSearchPhrase(
            resultProperties.fulltextSearchPhrase || this.resolveGeocoderInputValue()
          );
          this.manualSearchPhrase = fulltextPhrase;
          if (this.geocoderInputEl && fulltextPhrase !== '') {
            this.geocoderInputEl.value = fulltextPhrase;
          }
          self.place = this.place || '';
        } else {
          self.place = null;
        }

        switch (resultType) {
          case 'place':
            self.place = result.text;
            break;
          case 'address':
            self.place = result.text + (result.address ? ` ${result.address}` : '');
            break;
          case 'poi':
            self.place = result.properties.address;
            break;
          case 'district':
            self.place = result.text;
            break;
          default:
            if (!isFulltextSuggestion) {
              self.place = '';
            }
        }
        self.position = MapboxService.LngLat(
          result.geometry.coordinates[0],
          result.geometry.coordinates[1],
        );
        this.$emit('geocode', self.position.lat, self.position.lng, self.place);
        // initiate search submit
        this.submitForm();
      });

      // important to set this flag
      this.geocoderInitialized = true;

      return true;
    },

    /**
     *
     * @param lngLat mapbox LngLat object
     * @returns {jQuery|*|{}}
     */
    reverseMapboxGeocode(lngLat) {
      // this reference for callback functions
      const self = this;
      const def = $.Deferred();

      // check mapbox api
      if (!MapboxService.apiAvailable()) {
        def.reject();
        return def;
      }

      if (lngLat) {
        console.log(`Mapbox API call: lat=${lngLat.lat}, lng=${lngLat.lng}`);
        // save current query settings
        const tmpLimit = this.geocoder.getLimit();
        const tmpTypes = this.geocoder.getTypes();
        // set query settings for single place retrieval
        this.geocoder.setLimit(1);
        this.geocoder.setTypes('place');
        // do the query
        this.geocoder.query(`${lngLat.lng}, ${lngLat.lat}`);
        // restore current query settings
        this.geocoder.setLimit(tmpLimit);
        this.geocoder.setTypes(tmpTypes);
        def.resolve();
      } else {
        def.reject();
        throw new Error('No coordinates given');
      }
      return def;
    },

    buildDefaultSubmitSearchParams() {
      const lat = Number.parseFloat(this.latitude);
      const lng = Number.parseFloat(this.longitude);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
        return null;
      }

      return {
        place: this.resolvePlaceForStorage(this.normalizedSubmitPlace || this.place),
        lng,
        lat,
        spatial_pt: `${lat},${lng}`,
      };
    },

    submitForm() {
      const self = this;
      xsrfUtils.fetchCsrfToken('search').done((token) => {
        const forceRelevanceSort = this.consumeForcedRelevanceSortFlag();
        const normalizedPlace = `${self.place || ''}`.trim();
        const hasExplicitGeocodeSelection = normalizedPlace !== '';
        const storedSearchParams = this.normalizeStoredSearchParams(PersistentStoreProxyService[GET_SEARCH_PARAMS]());
        const currentInputPhrase = this.resolveGeocoderInputValue();
        this.manualSearchPhrase = currentInputPhrase;
        const shouldPersistPhrase = !this.lastInteractionWasGeocoderSelection && currentInputPhrase !== '';

        let nextSearchParams;
        if (hasExplicitGeocodeSelection) {
          nextSearchParams = {
            place: self.resolvePlaceForStorage(self.place),
            lng: self.position.lng,
            lat: self.position.lat,
            spatial_pt: `${self.position.lat},${self.position.lng}`,
          };
        } else if (this.preferDefaultLocationOnSubmit) {
          nextSearchParams = this.buildDefaultSubmitSearchParams();
        } else if (!storedSearchParams) {
          nextSearchParams = {
            place: self.resolvePlaceForStorage(self.place),
            lng: self.position.lng,
            lat: self.position.lat,
            spatial_pt: `${self.position.lat},${self.position.lng}`,
          };
        } else {
          nextSearchParams = {
            ...storedSearchParams,
          };
        }
        if (!nextSearchParams) {
          return;
        }
        nextSearchParams.phrase = shouldPersistPhrase ? currentInputPhrase : '';
        if (self.selectedSearchRadius !== null) {
          nextSearchParams.spatial_d = self.selectedSearchRadius;
        } else if (self.normalizedDefaultSearchRadius !== null) {
          nextSearchParams.spatial_d = self.normalizedDefaultSearchRadius;
        } else if (Object.prototype.hasOwnProperty.call(nextSearchParams, 'spatial_d')) {
          delete nextSearchParams.spatial_d;
        }
        if (forceRelevanceSort) {
          nextSearchParams.sort = 'relevance';
        } else if (Object.prototype.hasOwnProperty.call(nextSearchParams, 'sort')) {
          delete nextSearchParams.sort;
        }
        if (this.normalizedPresetProductType !== '') {
          nextSearchParams.product_type = [this.normalizedPresetProductType];
        } else {
          nextSearchParams.product_type = [];
        }
        PersistentStoreProxyService[SET_SEARCH_PARAMS]({
          ...nextSearchParams,
          reset_filters: true,
        });
        self.token = token;
        this.$nextTick(() => {
          // next tick to be sure the token value has been written in dom
          self.$form.submit();
        });
      }).fail(() => {
        console.error('token fetch failed');
      });
    },
  },
};
</script>

<style scoped>
.zero-section-geocoder-map .geocoder {
  flex: 1 1 auto;
  min-width: 0;
  box-shadow: 0 8px 22px rgba(0, 0, 0, 0.12);
  border-top-left-radius: 9999px;
  border-bottom-left-radius: 9999px;
}

.zero-section-geocoder-map .input-group-append {
  display: flex;
  margin-left: 0 !important;
}

.zero-section-geocoder-map .input-group-append .btn {
  margin-left: 0 !important;
  border-left: 0 !important;
  border-top-left-radius: 0 !important;
  border-bottom-left-radius: 0 !important;
  box-shadow: 0 8px 22px rgba(0, 0, 0, 0.12);
}

.geocoder {
  z-index: 1;
  margin: 0px;
}

::v-deep .mapboxgl-ctrl-geocoder {
  min-width: 100%;
  margin: 0 !important;
  box-shadow: none !important;
  border-right: 0 !important;
  border-top-right-radius: 0 !important;
  border-bottom-right-radius: 0 !important;
}

::v-deep .mapboxgl-ctrl-geocoder--input {
  margin: 0 !important;
}

::v-deep .mapboxgl-ctrl-geocoder--fulltext-separator {
  margin: 0 0 6px;
  padding: 4px 0 6px;
  background: #f3f3f3;
  border-bottom: 1px solid #e8e8e8;
  font-size: 11px;
  font-weight: 700;
  color: #6c757d;
  text-transform: none;
  line-height: 1.2;
}

::v-deep .mapboxgl-ctrl-geocoder .suggestions > .mapboxgl-ctrl-geocoder--powered-by > a {
  display: block;
  padding: 6px 12px 7px;
  border-top: 1px solid #ececec;
  font-size: 11px;
  font-weight: 400;
  letter-spacing: 0;
  color: #97a1ab !important;
  text-decoration: none;
  text-align: right;
  text-transform: none;
  opacity: 1;
}
</style>
